<template>
  <NuxtLayout
    v-if="campaign"
    :name="setLayout"
  >
    <div class="pb-12">
      <TranslationTool>
        <CopyField
          class="w-full"
          :text="campaignEndpoint"
        />
      </TranslationTool>
      <div
        v-if="temporaryBanner === 'lufthansa'"
        class="mt-6"
      >
        <LazyLufthansaBanner />
      </div>
      <RolfsLastMinuteCampaignVariant
        v-if="campaignVariant === 'last-minute' || campaignVariant === 'airport'"
        :campaign="campaign"
      />
      <RolfsDefaultCampaignVariant
        v-else
        :campaign="campaign"
        :first-trip="firstTrip"
      />
    </div>
  </NuxtLayout>
</template>

<script setup>
const localeStore = useLocaleStore()
const calendarStore = useCalendarStore()
const rootStore = useRootStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { urls: localeURLs } = useLocale()

const { error, data } = await useAsyncData(async () => {
  try {
    const campaignEndpoint = `/${localeStore.locale}/campaign/${route.params.name}`

    const { data: campaign } = await apiFetch(campaignEndpoint)

    const campaignVariant = campaign.campaign_type

    const pageBanner = {
      title: campaign.topic,
      subtitle: campaign.sub_title,
      background: {
        image: {
          url: campaign.hero_image_cloudinary,
        },
      },

      type: 'default',
      linkButton: campaign.call_to_action_text ? {
        text: campaign.call_to_action_text,
        href: 'campaignActionBtnScrollToElement',
        bgColor: 'blue',
      } : null,
    }
    calendarStore.SET_CAMPAIGN_ID(campaign?.id)
    calendarStore.SET_CAMPAIGN_VARIANT(campaignVariant)
    if (campaignVariant === 'last-minute' || campaignVariant === 'airport') {
      pageBanner.subtitleColor = 'orange'
      pageBanner.subtitleWeight = 'bold'

      if (campaign.origin_airports?.length) {
        calendarStore.SET_FORCE_AIRPORT(campaign.origin_airports[0])
      }

      await Promise.all([
        calendarStore.resetFilters(false),
        calendarStore.setFromBackupStateQuery(route.fullPath),
        calendarStore.getCalendarOptions(),
      ])
    }

    rootStore.SET_PAGE_BANNER(pageBanner)

    localeStore.SET_PAGE_WIDE_TRANSLATIONS(
      createPageWideTranslation(
        ['calenderHomeDescription', 'siteTitleBrand'],
        ['topic', 'description', 'page'],
        campaign
      )
    )

    return {
      campaign,
      campaignVariant,
      firstTrip: campaign?.triptypes?.[0] || null,
      campaignEndpoint,
    }
  } catch (e) {
    throw new Error('Post not found: ' + router.currentRoute.value.fullPath)
  }
})

if (error.value) {
  throw createError({
    message: error.value.message,
    statusCode: 404,
    stack: error.value.stack,
    fatal: true,
  })
}

const { campaign, campaignVariant, firstTrip, campaignEndpoint } = data.value

useHead({
  title: () => campaign.topic,
  meta: () => [
    {
      hid: 'og:url',
      property: 'og:url',
      content: () => `${localeURLs.BASE_URL}${localeURLs.campaign}/${route.params.name}`,
    },
    {
      hid: 'og:title',
      property: 'og:title',
      content: `${campaign.topic}`,
    },
    {
      hid: 'og:image',
      property: 'og:image',
      content: () => formatCloudinaryImagePath(firstTrip?.main_picture_cloudinary || campaign?.hero_image_cloudinary, {
        transformation: { watermark: true },
        forceJPG: true,
        isSol: false,
      }),
    },
    {
      hid: 'og:description',
      name: 'og:description',
      content: () => t('calenderHomeDescription'),
    },
    {
      hid: 'description',
      name: 'description',
      content: () => t('calenderHomeDescription'),
    },
  ],
})
const setLayout = computed(() => {
  return isSol() ? 'charter-page' : 'banner-default'
})

const temporaryBanner = computed(() => {
  const showBannerForCampaignId = {
    rolfsbuss: {
      // till-italien
      132: 'lufthansa',
    },

    solresor: {},
  }

  const bannersForLocale = showBannerForCampaignId[isSol() ? 'solresor' : 'rolfsbuss']
  return bannersForLocale[campaign.id]
})

onBeforeUnmount(() => {
  calendarStore.SET_CAMPAIGN_ID(null)
  calendarStore.SET_CAMPAIGN_VARIANT(null)
  calendarStore.SET_FORCE_AIRPORT(null)
})
</script>

<style lang="scss">
@import "@layers/web/assets/scss/modules/product/_product-trip-result";

.compaign_content {
  .searchresult {
    margin: 40px 0 !important;
  }
}

.top-section {
  @media (max-width: theme('screens.lg')) {
    margin-top: 0;
  }
}

/*! purgecss start ignore */
.campaign-preamble {
  h1, h2, h3 {
    // Chunk of HTML from Filemaker, hence no helper classes
    font-family: theme('fontFamily.serif');
    margin-bottom: 1rem;
  }
}
/*! purgecss end ignore */
</style>
